// src/services/dhyanaService.js
// API-only implementation via Cloudflare Worker proxy (no client key)

const DHYANA_API_BASE =
  process.env.REACT_APP_DHYANA_BRAIN_URL || "https://dhyana-prod.onrender.com";
const DEBUG_MODE = String(process.env.REACT_APP_DEBUG_MODE) === "true";

// Controlled logging
const log = DEBUG_MODE ? console.log : () => {};
const logError = console.error;

// In-memory caches
const contentCache = new Map();

// Cache analytics
let cacheHits = 0;
let cacheMisses = 0;

// Configuration state
let appConfig = {
  personas: null,
  stages: null,
  loaded: false,
  loadedAt: null,
};

// Cache configuration
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

// Persona key shape validation (format-only fallback)
const VALID_AUTHORITIES = ["high", "medium", "low"];
const VALID_DEPTHS = ["high", "medium", "low", "balanced"];

// Abort controllers
const activeRequests = new Map();

// --- helpers: robust parsing & normalization ---
function normalizePersonaKey(input) {
  if (!input) return null;
  let k = String(input).trim().toLowerCase();

  // unify separators
  k = k.replace(/[\s-]+/g, "_");

  // legacy -> canonical map (mirror of backend normalization)
  const legacyMap = {
    // legacy 2x2-ish
    high_high: "high_technical",
    high_medium: "high_balanced",
    high_low: "high_business",

    medium_high: "medium_technical",
    medium_medium: "medium_balanced",
    medium_low: "medium_business",

    low_high: "low_technical",
    low_medium: "low_balanced",
    low_low: "low_business",
  };

  return legacyMap[k] || k;
}

// parse personas payload in both Phase-4 and legacy shapes
// eslint-disable-next-line no-unused-vars
function extractPersonaKeys(json) {
  // Phase-4 canonical path
  const v4 = json?.data?.personas;
  if (v4 && typeof v4 === "object") return Object.keys(v4);

  // legacy safety valve (older responses)
  const legacy = json?.personas;
  if (legacy && typeof legacy === "object") return Object.keys(legacy);

  return [];
}

// parse stages payload in both Phase-4 and legacy shapes
// eslint-disable-next-line no-unused-vars
function extractStagesData(json) {
  // Phase-4 canonical path
  const v4 = json?.data?.systems;
  if (v4 && typeof v4 === "object") return v4;

  // legacy safety valve
  const legacy = json?.stages;
  if (legacy && typeof legacy === "object") return legacy;

  return {};
}

// --- helpers for safer parameter handling ---
function getSessionId() {
  const key = "dhyana_session_id";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `sess_${Math.random().toString(36).slice(2)}_${Date.now().toString(
      36,
    )}`;
    sessionStorage.setItem(key, id);
  }
  return id;
}

function buildUrlParams() {
  const sp = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const raw = Object.fromEntries(sp.entries());

  // normalize & prune empties
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v == null) continue;
    const trimmed = String(v).trim();
    if (trimmed === "") continue;
    out[k] = trimmed;
  }

  // coerce known numeric fields
  if (out.seniority !== undefined) {
    const n = parseInt(out.seniority, 10);
    if (Number.isFinite(n)) out.seniority = n;
    else delete out.seniority;
  }

  return out;
}

function safeReferrer() {
  if (typeof document !== "undefined" && typeof window !== "undefined") {
    // If document.referrer is empty (first-party loads), use window.location.origin as fallback
    return document.referrer && document.referrer.trim()
      ? document.referrer
      : window.location.origin;
  }
  return "";
}

async function withTimeout(promise, ms = 4500) {
  return await Promise.race([
    promise,
    new Promise((_, rej) =>
      setTimeout(() => rej(new Error(`timeout_${ms}ms`)), ms),
    ),
  ]);
}

// Context-aware helpers for personalization
const CONTEXT_PARAMS = ["job_title", "seniority", "industry", "company_size"];

function getUrlParams() {
  const params = buildUrlParams(); // Reuse existing parser
  // Filter to only context params we care about
  const context = {};
  CONTEXT_PARAMS.forEach((param) => {
    if (params[param]) {
      context[param] = params[param];
    }
  });
  return context;
}

function cacheKeyFor(stage, personaKey, contextObj) {
  const parts = [`stage:${stage}`, `persona:${personaKey}`];
  // Add context params in consistent order
  CONTEXT_PARAMS.forEach((param) => {
    if (contextObj && contextObj[param]) {
      parts.push(`${param}:${contextObj[param]}`);
    }
  });
  return parts.join("|");
}

// Helper to pick first non-empty value (moved to top level)
const pick = (...vals) =>
  vals.find((v) => v != null && String(v).trim() !== "");

// No hardcoded persona constants. All config is fetched from the API.
export const dhyanaService = {
  /**
   * Load personas + stages from API (call at startup)
   */
  async loadConfig() {
    if (appConfig.loaded) {
      log("Config already loaded");
      return appConfig;
    }

    try {
      const [personasRes, stagesRes] = await Promise.allSettled([
        fetch(`${DHYANA_API_BASE}/api/v1/config/personas`, {
          credentials: "omit",
        }),
        fetch(`${DHYANA_API_BASE}/api/v1/config/stages`, {
          credentials: "omit",
        }),
      ]);

      const personasOk =
        personasRes.status === "fulfilled" && personasRes.value.ok;
      const stagesOk = stagesRes.status === "fulfilled" && stagesRes.value.ok;
      if (!personasOk || !stagesOk) {
        const ps = personasOk ? personasRes.value.status : "ERR";
        const ss = stagesOk ? stagesRes.value.status : "ERR";
        console.warn(
          `Config load partial: personas=${ps}, stages=${ss} (frontend will use fallbacks where needed)`,
        );
      }

      const personasJson = personasOk
        ? await personasRes.value.json()
        : { data: { personas: {} } };
      const stagesJson = stagesOk
        ? await stagesRes.value.json()
        : { data: { systems: {} } };

      // Phase-4 contract puts personas under data.personas; keep legacy fallback
      const personas =
        personasJson?.data?.personas ?? personasJson?.personas ?? {};
      const systems = stagesJson?.data?.systems || {};
      const personaKeys = Object.keys(personas);
      const stagesData = systems;

      // If we have no personas due to API failure, provide fallback
      const finalPersonaKeys =
        personaKeys.length > 0 ? personaKeys : ["medium_balanced"];
      const finalStagesData =
        Object.keys(stagesData).length > 0
          ? stagesData
          : { 1: "awareness", 2: "consideration", 3: "decision" };

      appConfig = {
        personas: finalPersonaKeys, // Store as array of keys for easy validation
        stages: finalStagesData,
        loaded: true,
        loadedAt: new Date().toISOString(),
        fallback: !personasOk || !stagesOk, // Set fallback if either API failed
      };

      log("Config loaded", {
        personas: finalPersonaKeys,
        stagesCount: Object.keys(finalStagesData).length,
      });

      return appConfig;
    } catch (err) {
      logError("Failed to load config:", err);

      // Generic, non-persona fallback
      appConfig = {
        personas: ["medium_balanced"], // Default canonical persona array
        stages: { 1: "awareness", 2: "consideration", 3: "decision" },
        loaded: true, // We do load with fallback data
        loadedAt: new Date().toISOString(),
        fallback: true,
      };
      return appConfig;
    }
  },

  getConfig() {
    return appConfig;
  },

  /**
   * Generate landing page content
   * GET /api/v1/content/{stage}?persona={personaKey}
   */
  async generateLandingContent(stage, personaKey, signal = null) {
    // Resolve persona key with normalization and graceful fallback
    const resolvedPersona = this._resolvePersonaKey(personaKey);

    if (!this._validateStage(stage)) {
      logError("Invalid stage:", stage);
      return this._getFallbackContent(stage, personaKey, "invalid_stage");
    }

    // Get context from URL params (job_title, seniority, etc.)
    const ctx = getUrlParams();

    // Cache key now includes context for proper differentiation
    const cacheKey = cacheKeyFor(stage, resolvedPersona, ctx);
    const cached = contentCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      cacheHits++;
      log("Cache hit:", cacheKey);
      return cached.data;
    }
    cacheMisses++;

    const abortController = new AbortController();
    const requestId = `${stage}_${personaKey}_${Date.now()}`;
    const requestSignal = signal || abortController.signal;
    activeRequests.set(requestId, abortController);

    try {
      // Build URL with persona and context parameters
      const qs = new URLSearchParams({ persona: resolvedPersona });

      // Pass whitelisted context through to the backend
      if (ctx.job_title) qs.set("job_title", ctx.job_title);
      if (ctx.seniority) qs.set("seniority", ctx.seniority);
      if (ctx.industry) qs.set("industry", ctx.industry);
      if (ctx.company_size) qs.set("company_size", ctx.company_size);

      const url = `${DHYANA_API_BASE}/api/content/${encodeURIComponent(
        stage,
      )}?${qs.toString()}`;

      log("Fetching content with context:", {
        stage,
        originalPersona: personaKey,
        resolvedPersona,
        context: ctx,
      });

      const res = await fetch(url, { method: "GET", signal: requestSignal });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          `Content API failed: ${res.status} ${res.statusText} - ${text}`,
        );
      }

      const raw = await res.json();

      // Normalize backend → frontend shape with robust fallbacks
      const contentValue = pick(
        raw.body_content,
        raw.body,
        raw.subheadline,
        "AI-powered marketing intelligence tailored for your needs.",
      );

      console.log("🔍 Content mapping debug:", {
        raw_body_content: raw.body_content,
        raw_subheadline: raw.subheadline,
        mapped_content: contentValue,
        content_length: contentValue?.length,
      });

      const normalized = {
        headline: pick(
          raw.headline,
          raw.title,
          `Strategic Insights - Stage ${stage}`,
        ),
        content: contentValue,
        cta: pick(raw.cta_text, raw.cta, "Learn More"),
        stage: raw.stage ?? stage,
        persona: raw.persona ?? raw.personaKey ?? resolvedPersona,
        template: raw.template ?? "default",
        fallback: false, // Explicitly false for successful API responses
        generated_at: new Date().toISOString(),
        source: "ai_generated",

        // Include context fields for debugging/UI display
        job_title: pick(raw.job_title, ctx.job_title, ""),
        seniority: pick(raw.seniority, ctx.seniority, ""),
        industry: pick(raw.industry, ctx.industry, ""),
        company_size: pick(raw.company_size, ctx.company_size, ""),
      };

      // Cache the normalized version, not raw
      contentCache.set(cacheKey, { data: normalized, timestamp: Date.now() });
      return normalized;
    } catch (err) {
      if (err.name === "AbortError") {
        log("Request cancelled:", requestId);
        throw err;
      }
      logError("AI content generation failed:", {
        stage,
        personaKey,
        error: err.message,
      });
      return this._getFallbackContent(stage, personaKey, "api_error");
    } finally {
      activeRequests.delete(requestId);
    }
  },

  /**
   * Classify visitor persona (safe version with proper defaults)
   * POST /api/classify
   */
  async classifyPersona(overrides = {}) {
    // Build safe defaults if caller passes nothing:
    const urlParams = overrides.urlParams ?? buildUrlParams();
    const sessionId = overrides.sessionId ?? getSessionId();
    const userAgent =
      overrides.userAgent ??
      (typeof navigator !== "undefined" ? navigator.userAgent : "unknown");
    const referrer = overrides.referrer ?? safeReferrer();

    try {
      const res = await withTimeout(
        fetch(`${DHYANA_API_BASE}/api/classify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ urlParams, sessionId, userAgent, referrer }),
        }),
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Classification failed: ${res.status} - ${txt}`);
      }
      return await res.json();
    } catch (err) {
      console.error("Persona classification failed:", err);
      throw err;
    }
  },

  /**
   * Newsletter signup
   * POST /api/v1/subscriptions
   */
  async submitSignup(email, persona) {
    try {
      const res = await fetch(`${DHYANA_API_BASE}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, persona }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          `Newsletter signup failed: ${res.status} ${res.statusText} - ${text}`,
        );
      }

      return await res.json();
    } catch (err) {
      logError("Newsletter signup failed:", err);
      throw err;
    }
  },

  /**
   * Helpers
   */
  _getPersonaStages(personaKey) {
    const stageMap = {
      high_technical: [1, 4, 7, 10, 13],
      high_balanced: [1, 3, 6, 9, 12],
      high_business: [1, 2, 5, 8, 11],
      medium_technical: [2, 4, 8, 11, 14],
      medium_balanced: [2, 5, 7, 10, 13],
      medium_business: [3, 6, 8, 12, 14],
      low_technical: [3, 6, 9, 11, 14],
      low_balanced: [4, 7, 9, 12, 14],
      low_business: [5, 8, 10, 13, 14],
    };
    return stageMap[personaKey] || [1, 2, 5, 7, 10, 13];
  },

  // New approach: resolve persona with normalization and graceful fallback
  _resolvePersonaKey(rawKey) {
    const DEFAULT_PERSONA = "medium_balanced";

    const normalized = normalizePersonaKey(rawKey);
    if (!normalized) return DEFAULT_PERSONA;

    // Accept if present in server's current list
    if (appConfig.loaded && Array.isArray(appConfig.personas)) {
      if (appConfig.personas.includes(normalized)) {
        return normalized;
      }
    }

    // Warn and fall back instead of throwing (use console.warn not logError to avoid spam)
    if (rawKey !== normalized) {
      console.warn(
        `[dhyana] Persona key "${rawKey}" normalized to "${normalized}" (using fallback: ${DEFAULT_PERSONA})`,
      );
    } else {
      console.warn(
        `[dhyana] Unrecognized persona key "${rawKey}" (using fallback: ${DEFAULT_PERSONA})`,
      );
    }
    return DEFAULT_PERSONA;
  },

  // Legacy validation (kept for backward compatibility)
  _validatePersonaKey(personaKey) {
    if (!personaKey || typeof personaKey !== "string") return false;

    const normalized = normalizePersonaKey(personaKey);
    if (appConfig.loaded && Array.isArray(appConfig.personas)) {
      return appConfig.personas.includes(normalized);
    }

    // format-only fallback
    const parts = normalized.split("_");
    if (parts.length !== 2) return false;
    const [authority, depth] = parts;
    return (
      VALID_AUTHORITIES.includes(authority) && VALID_DEPTHS.includes(depth)
    );
  },

  _validateStage(stage) {
    if (appConfig.loaded && appConfig.stages) {
      // Check if stage exists in any of the stage systems (Phase-4 format)
      const stageStr = String(stage);
      for (const system of Object.values(appConfig.stages)) {
        if (system && typeof system === "object" && stageStr in system) {
          return true;
        }
      }
      return false;
    }
    return Number.isInteger(stage) && stage >= 1 && stage <= 14;
  },

  _getFallbackContent(stage, personaKey, reason = "api_error") {
    const ctx = getUrlParams();
    const personaName = ctx.job_title || "Marketing Professional";

    return {
      headline: `Strategic Marketing Intelligence - Stage ${stage}`,
      content: `Discover how advanced marketing intelligence can transform your decision-making. Tailored insights for ${personaName} leaders who demand data-driven results.`,
      cta: "Get Strategic Insights",
      template: "default",
      generated_at: new Date().toISOString(),
      stage,
      persona: personaKey,
      source: "fallback",
      fallback: true,
      fallbackReason: reason,

      // Include context fields even in fallback
      job_title: ctx.job_title || "",
      seniority: ctx.seniority || "",
      industry: ctx.industry || "",
      company_size: ctx.company_size || "",
    };
  },

  cancelAllRequests() {
    activeRequests.forEach((controller, id) => {
      controller.abort();
      log("Cancelled request:", id);
    });
    activeRequests.clear();
  },

  getCacheStats() {
    const total = cacheHits + cacheMisses;
    const hitRate = total ? ((cacheHits / total) * 100).toFixed(2) : "0.00";
    return {
      size: contentCache.size,
      hits: cacheHits,
      misses: cacheMisses,
      hitRate: `${hitRate}%`,
      activeRequests: activeRequests.size,
    };
  },

  clearCache() {
    contentCache.clear();
    cacheHits = 0;
    cacheMisses = 0;
    log("Content cache cleared");
  },
};

export default dhyanaService;
